import React, { Fragment, useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { Container } from "reactstrap";
import { C, reportingGlobalCSS, fieldLabel, dateInput } from "./reportingStyles";
import { Tile, RCard, Loader } from "./ReportingShared";
import { DATE_PRESETS, daysAgo, ymdLocal } from "./reportingFilters";
import { getCustomerAreas } from "../../utils/apiCalls";
import { isMerchantScoped } from "../../constants/roles";

/**
 * Customer Areas — where customers open the Pickup App from.
 *
 * Each square is a ~100 m grid cell the app sent a location from while browsing branches,
 * coloured by how many such lookups came from it. Anonymous by design: cells and counts
 * only, no customer or exact coordinate. A "lookup" is roughly one app session with
 * location — NOT a unique customer and NOT an order — and the copy below says so.
 *
 * Draws with the Google Maps JS already loaded globally (public/index.html), the same way
 * branches/add-branch.js does. Rectangles need no extra Maps library.
 */

// Cool → hot. Colour is chosen on a LOG scale of the count, so one very busy square (a mall,
// the office) does not wash every other square out to the palest shade.
const HEAT_STOPS = ["#b2f5ea", "#009689", "#f59e0b", "#dc2626"];

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const heatColor = (t) => {
  const x = Math.min(1, Math.max(0, t)) * (HEAT_STOPS.length - 1);
  const i = Math.min(HEAT_STOPS.length - 2, Math.floor(x));
  const f = x - i;
  const a = hexToRgb(HEAT_STOPS[i]);
  const b = hexToRgb(HEAT_STOPS[i + 1]);
  const mix = a.map((v, k) => Math.round(v + (b[k] - v) * f));
  return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
};

const intensity = (lookups, max) => (max <= 1 ? 1 : Math.log(lookups) / Math.log(max));

const KSA_CENTER = { lat: 23.8859, lng: 45.0792 };
// A single square would otherwise fitBounds to street level.
const MAX_FIT_ZOOM = 15;

const fmt = (n) => Number(n || 0).toLocaleString();

const CustomerAreasPage = () => {
  const userType = localStorage.getItem("userType");
  const merchantIdFromStorage = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = isMerchantScoped(userType) ? merchantIdFromStorage : reduxSelectedMerchant;
  const hasMerchant = Boolean(selectedMerchant) && selectedMerchant !== "all";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const [datePreset, setDatePreset] = useState("30d");
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(ymdLocal());

  const mapRef = useRef(null);
  const map = useRef(null);
  const overlays = useRef([]);
  const infoWindow = useRef(null);
  const mapsAvailable = typeof window !== "undefined" && Boolean(window.google?.maps);

  const datesReversed = Boolean(startDate && endDate && startDate > endDate);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const found = DATE_PRESETS.find((p) => p.value === preset);
    if (found) {
      const [s, e] = found.range();
      setStartDate(s || "");
      setEndDate(e || "");
    }
  };

  // A different merchant's squares must never be drawn under the new merchant's name.
  useEffect(() => {
    setData(null);
  }, [selectedMerchant]);

  // Debounced so clicking through presets sends one request; a stale response never
  // overwrites a newer one.
  const req = useRef(0);
  useEffect(() => {
    if (!hasMerchant || datesReversed) return;
    const t = setTimeout(async () => {
      const id = ++req.current;
      setLoading(true);
      try {
        const filters = {};
        if (startDate) filters.startDate = startDate;
        if (endDate) filters.endDate = endDate;
        const res = await getCustomerAreas(selectedMerchant, filters);
        if (id === req.current && res) setData(res);
      } catch (e) {
        console.error("Failed to fetch customer areas:", e);
      }
      if (id === req.current) setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [selectedMerchant, hasMerchant, datesReversed, startDate, endDate]);

  // The map div stays mounted for the page's lifetime (the loader is an overlay, not a
  // replacement), so the map is created once and survives every refetch.
  useEffect(() => {
    if (!hasMerchant || !mapsAvailable || !mapRef.current || map.current) return;
    map.current = new window.google.maps.Map(mapRef.current, {
      center: KSA_CENTER,
      zoom: 5,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
    });
    infoWindow.current = new window.google.maps.InfoWindow();
  }, [hasMerchant, mapsAvailable]);

  const maxLookups = useMemo(
    () => (data?.squares || []).reduce((m, s) => Math.max(m, s.lookups), 0),
    [data]
  );

  // Redraw on every new result: clear the previous overlays first, or squares from an older
  // range would stay on the map underneath the new ones.
  useEffect(() => {
    const g = window.google?.maps;
    if (!g || !map.current) return;

    overlays.current.forEach((o) => o.setMap(null));
    overlays.current = [];
    infoWindow.current?.close();
    if (!data) return;

    const half = (data.cellSizeDeg || 0.001) / 2;
    const bounds = new g.LatLngBounds();
    let hasBounds = false;

    for (const sq of data.squares || []) {
      const rect = new g.Rectangle({
        map: map.current,
        bounds: { north: sq.lat + half, south: sq.lat - half, east: sq.lng + half, west: sq.lng - half },
        fillColor: heatColor(intensity(sq.lookups, maxLookups)),
        fillOpacity: 0.6,
        strokeWeight: 0,
        clickable: true,
      });
      rect.addListener("click", () => {
        infoWindow.current.setContent(
          `<div style="font-family:inherit;font-size:13px;padding:2px 4px">
             <strong>${fmt(sq.lookups)}</strong> lookup${sq.lookups === 1 ? "" : "s"}
             <div style="color:#64748B;font-size:11px;margin-top:2px">~100 m area</div>
           </div>`
        );
        infoWindow.current.setPosition({ lat: sq.lat, lng: sq.lng });
        infoWindow.current.open(map.current);
      });
      overlays.current.push(rect);
      bounds.extend({ lat: sq.lat, lng: sq.lng });
      hasBounds = true;
    }

    for (const b of data.branches || []) {
      // A dark dot rather than the default red pin, which would read as one more hot square.
      const marker = new g.Marker({
        map: map.current,
        position: { lat: b.lat, lng: b.lng },
        title: b.name,
        icon: {
          path: g.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: "#1e293b",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
        zIndex: 1000,
      });
      overlays.current.push(marker);
      bounds.extend({ lat: b.lat, lng: b.lng });
      hasBounds = true;
    }

    if (hasBounds) {
      map.current.fitBounds(bounds);
      g.event.addListenerOnce(map.current, "idle", () => {
        if (map.current.getZoom() > MAX_FIT_ZOOM) map.current.setZoom(MAX_FIT_ZOOM);
      });
    } else {
      map.current.setCenter(KSA_CENTER);
      map.current.setZoom(5);
    }
  }, [data, maxLookups]);

  if (!hasMerchant) {
    return (
      <Fragment>
        <Breadcrumb title="Customer Areas" parent="Reporting" />
        <Container fluid>
          <div className="rpt-wrap" style={{ padding: 40, textAlign: "center", color: C.textMed }}>
            <style>{reportingGlobalCSS}</style>
            Please select a merchant to view reporting data.
          </div>
        </Container>
      </Fragment>
    );
  }

  const busiest = data?.squares?.[0];
  const empty = data && (data.squares || []).length === 0;

  return (
    <Fragment>
      <Breadcrumb title="Customer Areas" parent="Reporting" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">

          {/* ─── Filter Bar ─────────────────────────────── */}
          <RCard style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 140 }}>
                <div style={fieldLabel}>Date Range</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {DATE_PRESETS.map((p) => (
                    <div key={p.value} onClick={() => handlePresetChange(p.value)}
                      style={{
                        padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: datePreset === p.value ? 700 : 500,
                        cursor: "pointer", border: datePreset === p.value ? `1.5px solid ${C.coral}` : `1.5px solid ${C.border}`,
                        background: datePreset === p.value ? C.coralLight : C.white,
                        color: datePreset === p.value ? C.coral : C.textMed, whiteSpace: "nowrap",
                      }}>{p.label}</div>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div>
                  <div style={fieldLabel}>From</div>
                  <input type="date" value={startDate} style={dateInput}
                    onChange={(e) => { setStartDate(e.target.value); setDatePreset("custom"); }} />
                </div>
                <div>
                  <div style={fieldLabel}>To</div>
                  <input type="date" value={endDate} style={dateInput}
                    onChange={(e) => { setEndDate(e.target.value); setDatePreset("custom"); }} />
                </div>
              </div>
            </div>
            {datesReversed && (
              <div style={{ marginTop: 10, fontSize: 12, color: C.danger || "#b42318" }}>
                "From" is after "To" — adjust the range to refresh the map.
              </div>
            )}
          </RCard>

          {/* ─── KPIs ───────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
            <Tile label="Lookups" value={fmt(data?.totalLookups)} sub="App opens with location" />
            <Tile label="Areas" value={fmt(data?.squareCount)} sub="~100 m squares" delay={60} />
            <Tile label="Busiest Area" value={busiest ? fmt(busiest.lookups) : "—"} sub="Lookups from one square" delay={120} />
          </div>

          {/* ─── Map ────────────────────────────────────── */}
          <RCard title="Where customers open the app">
            <div style={{ fontSize: 12, color: C.textMed, marginBottom: 12 }}>
              Each square is a ~100 m area the Pickup App was opened from with location on.
              Counts are app lookups — not unique customers, and not orders. Days are UTC.
            </div>

            {!mapsAvailable ? (
              <div style={{ padding: 40, textAlign: "center", color: C.textMed }}>
                Google Maps could not be loaded, so the map is unavailable.
              </div>
            ) : (
              <div style={{ position: "relative" }}>
                <div ref={mapRef} style={{ width: "100%", height: 560, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}` }} />

                {loading && (
                  <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.55)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10 }}>
                    <Loader />
                  </div>
                )}

                {empty && !loading && (
                  <div style={{ position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)", background: C.white, padding: "8px 14px", borderRadius: 8, boxShadow: C.shadow, fontSize: 13, color: C.textMed }}>
                    No app lookups with location in this range yet.
                  </div>
                )}
              </div>
            )}

            {/* Legend */}
            <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", marginTop: 14, fontSize: 12, color: C.textMed }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>{maxLookups ? "1" : "Fewer"}</span>
                <div style={{ width: 160, height: 10, borderRadius: 5, background: `linear-gradient(90deg, ${HEAT_STOPS.join(", ")})` }} />
                <span>{maxLookups ? fmt(maxLookups) : "More"} lookups</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#1e293b", border: "2px solid #fff", boxShadow: "0 0 0 1px #cbd5e1", display: "inline-block" }} />
                Branch
              </div>
              {data?.truncated && (
                <div>Showing the {fmt(data.squares.length)} busiest areas of {fmt(data.squareCount)}.</div>
              )}
            </div>
          </RCard>
        </div>
      </Container>
    </Fragment>
  );
};

export default CustomerAreasPage;
